import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchPendingOrdersAction } from "../store/actions/orders";
import { enqueueSnackbar } from "notistack";
import { getDeviceId } from "../utils/deviceId";

export const usePendingOrdersInOrderWindow = ({ token, exchange, isOpen, orderSide, product, positionData, hasMatchingPosition }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  // Get pending orders for the same instrument
  const allPendingOrders = useSelector((state) => state.orders.arrPendingsData);
  
  // Check if this is a square-off order
  const isSquareOffOrder = hasMatchingPosition && positionData ? (() => {
    const matchingPosition = positionData.find(position => 
      (String(position.token) === String(token) ||
        String(position.instrument_token) === String(token)) &&
      position.net_quantity !== 0 && 
      position.product === product
    );
    
    if (matchingPosition) {
      const netQuantity = Number(matchingPosition.net_quantity) || 0;
      const positionSide = netQuantity < 0 ? "SELL" : "BUY";
      
      // If this is a square-off order (opposite direction of position)
      return (positionSide === "BUY" && orderSide === "SELL") || 
             (positionSide === "SELL" && orderSide === "BUY");
    }
    return false;
  })() : false;

  // Filter pending orders based on square-off status
  const instrumentPendingOrders = allPendingOrders.filter(order => {
    const matchesInstrument = (String(order.token) === String(token) || 
     String(order.instrument_token) === String(token)) &&
    order.exchange === exchange;
    
    if (!matchesInstrument) return false;
    
    // If it's a square-off order, show StopLoss orders (SL and SLM) and LIMIT orders
    if (isSquareOffOrder) {
      return order.order_type === 'SL' || 
             order.order_type === 'SLM' || 
             order.order_type === 'STOPLOSS' || 
             order.order_type === 'STOPLOSSMARKET' ||
             order.order_type === 'LIMIT';
    }
    
    // Otherwise show all orders for this instrument
    return true;
  });

  // Fetch pending orders when order window opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchPendingOrdersAction());
    }
  }, [isOpen, dispatch]);

  // Toggle expanded state
  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  // Handle cancel order
  const cancelOrder = useCallback(async (order, e) => {
    e.stopPropagation();
    if (cancelling === order.oms_order_id) return;
    
    setCancelling(order.oms_order_id);
    
    try {
      const loginId = localStorage.getItem("login_id");
      const authToken = localStorage.getItem("auth_token");
      
      if (!loginId) {
        enqueueSnackbar("User is not authenticated.", { variant: "error" });
        return;
      }
      
      const response = await fetch(
        `/api/v1/orders/${order.oms_order_id}?client_id=${loginId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-authorization-token": authToken,
            "x-device-type": "WEB",
            "x-device-id": getDeviceId(),
          },
        }
      );
      
      const data = await response.json();
      
      if (data.status === "success") {
        enqueueSnackbar(data.message || "Order cancelled successfully", { 
          variant: "success",
          autoHideDuration: 2000 
        });
        // Refresh pending orders
        dispatch(fetchPendingOrdersAction());
      } else {
        enqueueSnackbar(data.message || "Failed to cancel order", { variant: "error" });
      }
    } catch (err) {
      console.error("Order cancellation error:", err);
      enqueueSnackbar(err.message || "Failed to cancel order", { variant: "error" });
    } finally {
      setCancelling(null);
    }
  }, [cancelling, dispatch]);



  return {
    instrumentPendingOrders,
    expanded,
    cancelling,
    toggleExpanded,
    cancelOrder,
    hasOrders: instrumentPendingOrders.length > 0,
    isSquareOffOrder,
    stopLossOrdersCount: isSquareOffOrder ? instrumentPendingOrders.filter(order => 
      order.order_type === 'SL' || 
      order.order_type === 'SLM' || 
      order.order_type === 'STOPLOSS' || 
      order.order_type === 'STOPLOSSMARKET' ||
      order.order_type === 'LIMIT'
    ).length : 0
  };
}; 